/**
 * Break-even solvers — extracted from model.ts for Phase 15.
 *
 * Bisection solvers for operator break-even rent and enrolment.
 * Depends on computeCore from model.ts (circular import is safe in ESM —
 * computeCore is only called at runtime, not at module evaluation time).
 */

import type { Scenario, ModelResult } from "../types";

export const EBITDA_EPS = 1e-6; // EBITDA-zero break-even tolerance ($), sub-cent

// Synchronous access — set by model.ts at module init via _setComputeCore().
// This avoids circular import issues: model.ts calls _setComputeCore(computeCore)
// at module evaluation time, and the break-even functions only call computeCore
// at runtime (inside function bodies), by which point model.ts has fully loaded.
let _computeCoreSync: ((s: Scenario) => ModelResult) | null = null;
export function _setComputeCore(fn: (s: Scenario) => ModelResult) {
  _computeCoreSync = fn;
}

function clone<T>(o: T): T {
  return structuredClone(o);
}

/** P1-R2 — bisect a uniform netRentPerSqm scale until Year-1 operator EBITDA crosses 0.
 *  Operator break-even: "how much rent before the school goes EBITDA-negative."
 *  CRITICAL: evaluate() calls computeCore (non-recursive core), NOT compute. */
export function breakEvenRent(scenario: Scenario): {
  rentScale: number;
  breakEvenRentY1: number;
  converged: boolean;
} {
  if (!Array.isArray(scenario.buildings) || scenario.buildings.length === 0) {
    return { rentScale: 1, breakEvenRentY1: 0, converged: false };
  }
  const computeCore = _computeCoreSync!;
  const evaluate = (scale: number): { ebitda: number; rentGross: number } => {
    const trial = clone(scenario);
    for (const b of trial.buildings) {
      if (Number.isFinite(b.netRentPerSqm))
        b.netRentPerSqm = b.netRentPerSqm * scale;
    }
    const r = computeCore(trial);
    return {
      ebitda: r.years[0]?.reddamEbitda ?? Number.NaN,
      rentGross: r.years[0]?.rentGross ?? 0,
    };
  };
  let lo = 0,
    hi = 10;
  let fLo = evaluate(lo).ebitda;
  let fHi = evaluate(hi).ebitda;
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi) || fLo * fHi > 0) {
    const base = evaluate(1);
    return { rentScale: 1, breakEvenRentY1: base.rentGross, converged: false };
  }
  let mid = 1;
  let last = evaluate(mid);
  for (let i = 0; i < 60; i++) {
    mid = (lo + hi) / 2;
    last = evaluate(mid);
    if (Math.abs(last.ebitda) < EBITDA_EPS) break;
    if (last.ebitda * fLo < 0) hi = mid;
    else lo = mid;
  }
  return {
    rentScale: mid,
    breakEvenRentY1: last.rentGross,
    converged: Math.abs(last.ebitda) < EBITDA_EPS,
  };
}

/** P1-R3 — bisect steadyStateUtilisation in [0, base] until final-year operator EBITDA crosses 0.
 *  Operator demand-side break-even: "how few students before it breaks."
 *  CRITICAL: evaluate() calls computeCore (non-recursive core), NOT compute. */
export function breakEvenEnrolment(scenario: Scenario): {
  utilisation: number;
  enrolment: number;
  converged: boolean;
} {
  const baseUtil = scenario.assumptions?.steadyStateUtilisation ?? 0;
  if (
    !Array.isArray(scenario.buildings) ||
    scenario.buildings.length === 0 ||
    baseUtil <= 0
  ) {
    return { utilisation: baseUtil, enrolment: 0, converged: false };
  }
  const computeCore = _computeCoreSync!;
  const evaluate = (util: number): { ebitda: number; enrolment: number } => {
    const trial = clone(scenario);
    trial.assumptions.steadyStateUtilisation = util;
    const r = computeCore(trial);
    const last = r.years.at(-1);
    return {
      ebitda: last?.reddamEbitda ?? Number.NaN,
      enrolment: last?.enrolment ?? 0,
    };
  };
  let lo = 0,
    hi = baseUtil;
  let fLo = evaluate(lo).ebitda;
  let fHi = evaluate(hi).ebitda;
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi) || fLo * fHi > 0) {
    const base = evaluate(baseUtil);
    return {
      utilisation: baseUtil,
      enrolment: base.enrolment,
      converged: false,
    };
  }
  // EBITDA-zero tolerance in dollars. Must be < $0.5 so the verified final-year
  // reddamEbitda satisfies the test's toBeCloseTo(0, 0); module-scope EBITDA_EPS is sub-cent precision.
  let mid = baseUtil;
  let last = evaluate(mid);
  for (let i = 0; i < 60; i++) {
    mid = (lo + hi) / 2;
    last = evaluate(mid);
    if (Math.abs(last.ebitda) < EBITDA_EPS) break;
    if (last.ebitda * fLo < 0) hi = mid;
    else lo = mid;
  }
  return {
    utilisation: mid,
    enrolment: last.enrolment,
    converged: Math.abs(last.ebitda) < EBITDA_EPS,
  };
}
